import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, getImageSrc } from "@/lib/api";
import { toast } from "sonner";
import { Users, Package, ShoppingBag, TrendingUp, ShieldCheck, ShieldAlert, AlertTriangle, Check, X, Ban, Play } from "lucide-react";

const TABS = ["Overview", "Sellers", "Buyers", "Listings", "Orders", "Low stock"];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [productFilter, setProductFilter] = useState("all");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (!user.is_admin) { nav("/"); return; }
    reloadAll();
  }, [loading, user]);

  const reloadAll = async () => {
    try {
      const [s, u, p, o, ls] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/products"),
        api.get("/admin/orders"),
        api.get("/admin/stock/low"),
      ]);
      setStats(s.data); setUsers(u.data); setProducts(p.data); setOrders(o.data); setLowStock(ls.data);
    } catch (e) { toast.error(formatError(e)); }
  };

  const suspendUser = async (id, suspended) => {
    const reason = suspended ? window.prompt("Reason (optional):", "") : null;
    try {
      await api.post(`/admin/users/${id}/suspend`, { suspended, reason });
      toast.success(suspended ? "Account suspended" : "Account reactivated");
      reloadAll();
    } catch (e) { toast.error(formatError(e)); }
  };

  const verifySeller = async (id) => {
    try {
      await api.post(`/admin/users/${id}/verify`);
      toast.success("Seller verified");
      reloadAll();
    } catch (e) { toast.error(formatError(e)); }
  };

  const moderateProduct = async (id, status) => {
    const reason = status === "rejected" ? window.prompt("Rejection reason:", "") : null;
    try {
      await api.post(`/admin/products/${id}/moderate`, { status, reason });
      toast.success(`Listing ${status}`);
      reloadAll();
    } catch (e) { toast.error(formatError(e)); }
  };

  const suspendProduct = async (id, suspended) => {
    const reason = suspended ? window.prompt("Reason (optional):", "") : null;
    try {
      await api.post(`/admin/products/${id}/suspend`, { suspended, reason });
      toast.success(suspended ? "Listing suspended" : "Listing reinstated");
      reloadAll();
    } catch (e) { toast.error(formatError(e)); }
  };

  const updateOrder = async (id, status) => {
    try {
      await api.post(`/admin/orders/${id}/status`, { status });
      toast.success(`Order → ${status}`);
      reloadAll();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (loading || !user) return <div className="p-10 text-center text-[#5C6660]">Loading...</div>;
  if (!user.is_admin) return null;

  const sellersList = users.filter((u) => u.role === "seller" && !u.is_admin);
  const buyersList = users.filter((u) => u.role === "buyer");
  const productsFiltered = productFilter === "all" ? products
    : productFilter === "pending" ? products.filter((p) => p.moderation_status === "pending")
    : productFilter === "rejected" ? products.filter((p) => p.moderation_status === "rejected")
    : productFilter === "suspended" ? products.filter((p) => p.suspended)
    : products;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">AgriNuhse Admin</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1E4D3B] mt-1">Platform Control Center</h1>
        </div>
        <div className="text-xs uppercase tracking-widest font-bold text-white bg-[#1E4D3B] px-3 py-1 rounded-full">Admin session</div>
      </div>

      <div className="mt-6 flex flex-wrap gap-1 sm:gap-2 border-b border-[#E5D9C8]">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${tab === t ? "border-[#C85A32] text-[#C85A32]" : "border-transparent text-[#5C6660] hover:text-[#1E4D3B]"}`}
            data-testid={`admin-tab-${t.toLowerCase().replace(" ", "-")}`}
          >{t}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "Overview" && stats && (
        <div className="mt-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={TrendingUp} label="GMV (GH₵)" value={stats.gmv_ghs.toFixed(2)} testid="admin-stat-gmv" />
            <StatCard icon={ShoppingBag} label="Total orders" value={stats.orders} sub={`${stats.orders_today} today`} testid="admin-stat-orders" />
            <StatCard icon={Package} label="Listings" value={stats.products} sub={`${stats.pending_products} pending review`} testid="admin-stat-products" alert={stats.pending_products > 0} />
            <StatCard icon={Users} label="Users" value={stats.users} sub={`${stats.sellers} sellers · ${stats.buyers} buyers`} testid="admin-stat-users" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <StatCard icon={ShieldCheck} label="Verified sellers" value={stats.verified_sellers} testid="admin-stat-verified" />
            <StatCard icon={AlertTriangle} label="Low-stock listings" value={stats.low_stock_products} testid="admin-stat-lowstock" alert={stats.low_stock_products > 0} />
            <StatCard icon={Ban} label="Suspended" value={stats.suspended_users + stats.suspended_products} sub={`${stats.suspended_users} users · ${stats.suspended_products} listings`} testid="admin-stat-suspended" />
          </div>
        </div>
      )}

      {/* SELLERS */}
      {tab === "Sellers" && (
        <Section title={`Sellers (${sellersList.length})`}>
          <UserTable users={sellersList} onSuspend={suspendUser} onVerify={verifySeller} kind="seller" />
        </Section>
      )}

      {/* BUYERS */}
      {tab === "Buyers" && (
        <Section title={`Buyers (${buyersList.length})`}>
          <UserTable users={buyersList} onSuspend={suspendUser} kind="buyer" />
        </Section>
      )}

      {/* LISTINGS */}
      {tab === "Listings" && (
        <Section title={`Listings (${productsFiltered.length}/${products.length})`}>
          <div className="mb-3 flex flex-wrap gap-2">
            {["all", "pending", "rejected", "suspended"].map((f) => (
              <button key={f} onClick={() => setProductFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${productFilter === f ? "bg-[#1E4D3B] text-white" : "bg-[#F3ECE2] text-[#5C6660]"}`}
                data-testid={`admin-listing-filter-${f}`}
              >{f}</button>
            ))}
          </div>
          <div className="space-y-2">
            {productsFiltered.map((p) => (
              <div key={p.id} className="card-farm p-3 flex flex-wrap items-center gap-3" data-testid={`admin-product-${p.id}`}>
                <img src={getImageSrc(p)} alt={p.name} className="w-16 h-16 object-cover rounded-lg shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-bold text-[#1F2421]">{p.name}</div>
                  <div className="text-xs text-[#5C6660]">{p.seller_name} · {p.category} · {p.region} · GH₵ {p.price.toFixed(2)}</div>
                  <div className="flex gap-1.5 mt-1">
                    <StatusChip status={p.moderation_status || "approved"} />
                    {p.suspended && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D93838] text-white uppercase font-bold tracking-widest">Suspended</span>}
                    {p.stock <= 5 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D99B26] text-[#1F2421] uppercase font-bold tracking-widest">Low stock: {p.stock}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  {p.moderation_status !== "approved" && (
                    <button onClick={() => moderateProduct(p.id, "approved")} className="p-2 rounded bg-[#1B804E] text-white" title="Approve" data-testid={`admin-approve-${p.id}`}><Check className="w-4 h-4" /></button>
                  )}
                  {p.moderation_status !== "rejected" && (
                    <button onClick={() => moderateProduct(p.id, "rejected")} className="p-2 rounded bg-[#D93838] text-white" title="Reject" data-testid={`admin-reject-${p.id}`}><X className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => suspendProduct(p.id, !p.suspended)} className={`p-2 rounded ${p.suspended ? "bg-[#1B804E]" : "bg-[#5C6660]"} text-white`} title={p.suspended ? "Reinstate" : "Suspend"} data-testid={`admin-suspend-listing-${p.id}`}>
                    {p.suspended ? <Play className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
            {productsFiltered.length === 0 && <div className="text-center text-[#5C6660] py-8 text-sm">No listings in this view.</div>}
          </div>
        </Section>
      )}

      {/* ORDERS */}
      {tab === "Orders" && (
        <Section title={`All orders (${orders.length})`}>
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="card-farm p-4" data-testid={`admin-order-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-mono text-xs text-[#8C6D53]">{o.payment.reference}</div>
                    <div className="font-bold">{o.buyer_name} · {o.delivery_region}</div>
                    <div className="text-xs text-[#5C6660]">{new Date(o.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#C85A32] font-black text-lg">GH₵ {o.total.toFixed(2)}</div>
                    <StatusChip status={o.status} />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["processing", "shipped", "delivered", "cancelled"].map((s) => (
                    <button key={s} onClick={() => updateOrder(o.id, s)}
                      className={`px-3 py-1 rounded text-xs font-semibold capitalize ${o.status === s ? "bg-[#1E4D3B] text-white" : "bg-[#F3ECE2] text-[#1F2421] hover:bg-[#E5D9C8]"}`}
                      data-testid={`admin-order-${o.id}-${s}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
            ))}
            {orders.length === 0 && <div className="text-center text-[#5C6660] py-8 text-sm">No orders yet.</div>}
          </div>
        </Section>
      )}

      {/* LOW STOCK */}
      {tab === "Low stock" && (
        <Section title={`Low stock (≤ 5)`}>
          <div className="space-y-2">
            {lowStock.map((p) => (
              <div key={p.id} className="card-farm p-3 flex items-center gap-3" data-testid={`admin-lowstock-${p.id}`}>
                <img src={getImageSrc(p)} alt={p.name} className="w-14 h-14 object-cover rounded-lg" />
                <div className="flex-1">
                  <div className="font-bold text-[#1F2421]">{p.name}</div>
                  <div className="text-xs text-[#5C6660]">{p.seller_name} · GH₵ {p.price.toFixed(2)}</div>
                </div>
                <div className="text-2xl font-black text-[#D93838]">{p.stock}</div>
                <div className="text-xs uppercase tracking-widest text-[#8C6D53]">{p.unit}</div>
              </div>
            ))}
            {lowStock.length === 0 && <div className="text-center text-[#5C6660] py-8 text-sm">All listings well-stocked 🌾</div>}
          </div>
        </Section>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, sub, testid, alert }) => (
  <div className={`card-farm p-5 ${alert ? "border-[#D99B26]" : ""}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alert ? "bg-[#FFFBEE] text-[#D99B26]" : "bg-[#F3ECE2] text-[#C85A32]"}`}><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">{label}</div>
        <div className="text-2xl font-black text-[#1E4D3B]" data-testid={testid}>{value}</div>
        {sub && <div className="text-xs text-[#5C6660]">{sub}</div>}
      </div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mt-8">
    <h2 className="text-lg font-extrabold text-[#1E4D3B] mb-4">{title}</h2>
    {children}
  </div>
);

const StatusChip = ({ status }) => {
  const map = {
    approved: "bg-[#1B804E] text-white",
    pending: "bg-[#D99B26] text-[#1F2421]",
    rejected: "bg-[#D93838] text-white",
    processing: "bg-[#1B804E] text-white",
    shipped: "bg-[#00539B] text-white",
    delivered: "bg-[#1E4D3B] text-white",
    cancelled: "bg-[#D93838] text-white",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold tracking-widest ${map[status] || "bg-[#5C6660] text-white"}`}>{status}</span>;
};

const UserTable = ({ users, onSuspend, onVerify, kind }) => (
  <div className="space-y-2">
    {users.map((u) => (
      <div key={u.id} className="card-farm p-3 flex flex-wrap items-center gap-3" data-testid={`admin-user-${u.id}`}>
        <div className="w-10 h-10 rounded-full bg-[#1E4D3B] text-white flex items-center justify-center font-black shrink-0">{(u.name || "?")[0]}</div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-bold text-[#1F2421] inline-flex items-center gap-1.5">
            {u.name}
            {u.verified && <ShieldCheck className="w-4 h-4 text-[#1B804E]" />}
            {u.suspended && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D93838] text-white uppercase font-bold tracking-widest">Suspended</span>}
          </div>
          <div className="text-xs text-[#5C6660]">{u.email} · {u.phone || "no phone"} · {u.region || "—"}</div>
        </div>
        <div className="flex gap-2">
          {kind === "seller" && !u.verified && (
            <button onClick={() => onVerify(u.id)} className="px-3 py-1.5 rounded bg-[#1B804E] text-white text-xs font-semibold inline-flex items-center gap-1" data-testid={`admin-verify-${u.id}`}>
              <ShieldCheck className="w-3.5 h-3.5" /> Verify
            </button>
          )}
          <button onClick={() => onSuspend(u.id, !u.suspended)}
            className={`px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1 ${u.suspended ? "bg-[#1B804E] text-white" : "bg-[#D93838] text-white"}`}
            data-testid={`admin-suspend-user-${u.id}`}
          >
            {u.suspended ? <><Play className="w-3.5 h-3.5" /> Reactivate</> : <><Ban className="w-3.5 h-3.5" /> Suspend</>}
          </button>
        </div>
      </div>
    ))}
    {users.length === 0 && <div className="text-center text-[#5C6660] py-8 text-sm">No {kind}s yet.</div>}
  </div>
);
